/**
 * Auto-save engine for visit answers.
 * Tracks dirty answers, debounces, batches, and retries on failure.
 * Pure logic — no React dependency. The form screen calls these methods.
 *
 * Two commit paths. The caller picks by question type, not by guesswork:
 *
 *   - `markDirty`    — debounced. Typed input only (text, number, phone,
 *                      address, BP, body metrics). Saving per keystroke would
 *                      be one request per character.
 *   - `markDirtyNow` — commits immediately. Answers that can only change by a
 *                      discrete tap (radio, checkbox, yes/no, dropdown, date
 *                      picker). There is no keystroke stream to coalesce, so
 *                      the debounce window buys nothing and costs exposure.
 */

export interface PendingAnswer {
    questionId: string;
    value: string;
}

export interface AutoSaveConfig {
    /** Debounce delay in ms (default: 3000) */
    debounceMs?: number;
    /** Function that sends answers to the server. Returns true on success. */
    saveFn: (answers: PendingAnswer[]) => Promise<boolean>;
    /** Called when save status changes */
    onStatusChange?: (status: AutoSaveStatus) => void;
}

export type AutoSaveStatus = "idle" | "saving" | "error" | "saved";

export class AutoSaveEngine {
    private dirty = new Map<string, string>(); // questionId → value
    private timer: ReturnType<typeof setTimeout> | null = null;
    private debounceMs: number;
    private saveFn: (answers: PendingAnswer[]) => Promise<boolean>;
    private onStatusChange?: (status: AutoSaveStatus) => void;
    private _status: AutoSaveStatus = "idle";
    /** The pass whose request is currently in the air. See `flush()`. */
    private running: Promise<boolean> | null = null;
    /** At most one pass waiting its turn, not yet sent. See `flush()`. */
    private queued: Promise<boolean> | null = null;

    constructor(config: AutoSaveConfig) {
        this.debounceMs = config.debounceMs ?? 3000;
        this.saveFn = config.saveFn;
        this.onStatusChange = config.onStatusChange;
    }

    get status(): AutoSaveStatus {
        return this._status;
    }

    get dirtyCount(): number {
        return this.dirty.size;
    }

    private setStatus(status: AutoSaveStatus) {
        this._status = status;
        this.onStatusChange?.(status);
    }

    /**
     * Mark an answer as dirty and let the debounce carry it. Resets the timer.
     * For typed input — see the class comment.
     */
    markDirty(questionId: string, value: string) {
        this.dirty.set(questionId, value);
        this.scheduleFlush();
    }

    /**
     * Mark an answer as dirty and commit it now, without waiting out the
     * debounce. For tap answers — see the class comment.
     *
     * Returns the flush promise so a caller that needs the answer durable
     * (section complete, navigating away) can await it. Fire-and-forget is
     * fine for the tap itself: a failure re-queues via `scheduleFlush`.
     */
    markDirtyNow(questionId: string, value: string): Promise<boolean> {
        this.dirty.set(questionId, value);
        return this.flush();
    }

    /**
     * Schedule a flush after the debounce period.
     */
    private scheduleFlush() {
        if (this.timer) {
            clearTimeout(this.timer);
        }
        this.timer = setTimeout(() => void this.flush(), this.debounceMs);
    }

    /**
     * Flush all dirty answers to the server.
     * Called on debounce fire, on a tap answer, before the section AI triggers,
     * on section complete, and on unmount.
     *
     * Queued, not guarded. Exactly one request is ever in the air — that was
     * always the requirement, because a batch carries whole answer values and
     * the server applies them in arrival order, so two overlapping requests for
     * the same question can land out of order and leave the stale one winning.
     * An earlier version enforced it by *dropping* the colliding call (return
     * false, no reschedule) — and cleared the debounce timer before doing so,
     * stranding those answers entirely. Rare while everything waited out a 3s
     * debounce; routine once tap answers commit on touch, and there is no retry
     * behind a fire-and-forget tap.
     *
     * So collisions wait instead of being turned away, and the queue is capped
     * at one. A queued pass snapshots `dirty` when it *runs*, not when it was
     * created, so it already covers every answer dirtied while the current
     * request is in flight — one tap or fifty. A third pass could never carry
     * anything the second would not, which is why depth 2 is the real maximum
     * rather than a tuning choice. Fifty taps during one slow save produce two
     * requests, and a caller awaiting this waits for at most two.
     *
     * `true` means everything dirty at call time is committed: the server
     * commits its own short transaction before responding (`saveAnswersDurable`),
     * so it is durable, not merely accepted. That guarantee is what lets the
     * section AI triggers and Complete gate on this.
     */
    flush(): Promise<boolean> {
        // A queued pass has not started yet, so it will snapshot `dirty` after
        // this caller's write. Sharing it is both correct and what caps the depth.
        if (this.queued) {
            return this.queued;
        }
        if (this.running) {
            this.queued = this.running
                .catch(() => false)
                .then(() => {
                    this.queued = null;
                    return this.startPass();
                });
            return this.queued;
        }
        return this.startPass();
    }

    private startPass(): Promise<boolean> {
        const pass = this.runPass().finally(() => {
            if (this.running === pass) {
                this.running = null;
            }
        });
        this.running = pass;
        return pass;
    }

    /** One save attempt. Never called concurrently — `flush` serializes it. */
    private async runPass(): Promise<boolean> {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }

        if (this.dirty.size === 0) {
            return true;
        }

        this.setStatus("saving");

        // Snapshot current dirty answers
        const batch: PendingAnswer[] = [];
        const snapshot = new Map(this.dirty);
        for (const [questionId, value] of snapshot) {
            batch.push({ questionId, value });
        }

        try {
            const success = await this.saveFn(batch);
            if (success) {
                // Remove saved answers from dirty (only if they haven't changed since)
                for (const [questionId, value] of snapshot) {
                    if (this.dirty.get(questionId) === value) {
                        this.dirty.delete(questionId);
                    }
                }
                this.setStatus(this.dirty.size > 0 ? "idle" : "saved");
                return true;
            }
            // Keep in dirty queue for retry
            this.setStatus("error");
            this.scheduleFlush();
            return false;
        } catch {
            // Keep in dirty queue for retry
            this.setStatus("error");
            this.scheduleFlush();
            return false;
        }
    }

    /**
     * Clean up timers. Call when unmounting.
     */
    destroy() {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }
}
